import React, { useRef } from "react";
import { PAGE, fontCss, substitute } from "@/lib/studio/templates";
import { resolveSrc } from "@/lib/studio/render";

const PT_TO_MM = 25.4 / 72;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/**
 * WYSIWYG A4 page. Elements are absolutely positioned in mm × scale.
 * Drag anywhere on an element to move it; drag the amber corner handle
 * to resize (text: width only).
 */
export function StudioCanvas({ design, row, assets, scale, selectedId, onSelect, onUpdate }) {
  const drag = useRef(null);

  const start = (e, el, mode) => {
    e.stopPropagation();
    e.preventDefault();
    onSelect(el.id);
    drag.current = { id: el.id, mode, sx: e.clientX, sy: e.clientY, x: el.x, y: el.y, w: el.w, h: el.h, type: el.type };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const move = (e) => {
    const d = drag.current;
    if (!d) return;
    const dx = (e.clientX - d.sx) / scale;
    const dy = (e.clientY - d.sy) / scale;
    if (d.mode === "move") {
      onUpdate(d.id, {
        x: Math.round(clamp(d.x + dx, -d.w + 5, PAGE.w - 5) * 2) / 2,
        y: Math.round(clamp(d.y + dy, -5, PAGE.h - 5) * 2) / 2,
      });
    } else {
      const patch = { w: Math.round(Math.max(5, d.w + dx) * 2) / 2 };
      if (d.type !== "text" && d.type !== "line") patch.h = Math.round(Math.max(3, d.h + dy) * 2) / 2;
      onUpdate(d.id, patch);
    }
  };
  const end = () => { drag.current = null; };

  return (
    <div
      className="relative shadow-2xl select-none"
      style={{ width: PAGE.w * scale, height: PAGE.h * scale, background: design.background || "#fff" }}
      onPointerDown={() => onSelect(null)}
      data-testid="studio-canvas"
    >
      {design.elements.map((el) => {
        const sel = el.id === selectedId;
        const common = {
          position: "absolute", left: el.x * scale, top: el.y * scale, cursor: "move",
          outline: sel ? "1px dashed #f59e0b" : "none", outlineOffset: 2,
        };
        const tid = { "data-testid": `studio-el-${el.id}` };
        let body = null;
        if (el.type === "text") {
          body = (
            <div {...tid} style={{
              ...common, width: el.w * scale, minHeight: 8,
              fontFamily: fontCss(el.font), fontSize: (el.fontSize || 16) * PT_TO_MM * scale,
              fontWeight: el.bold ? 700 : 400, fontStyle: el.italic ? "italic" : "normal",
              color: el.color, textAlign: el.align || "left", lineHeight: 1.2,
              whiteSpace: "pre-wrap", wordBreak: "break-word",
            }}>
              {substitute(el.text, row) || (sel ? "" : "\u00a0")}
            </div>
          );
        } else if (el.type === "image") {
          const src = resolveSrc(el, assets);
          body = (
            <div {...tid} style={{ ...common, width: el.w * scale, height: el.h * scale }}>
              {src ? (
                <img src={src} alt="" draggable={false}
                  style={{ width: "100%", height: "100%", objectFit: "contain", pointerEvents: "none" }} />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[10px] uppercase tracking-wider"
                  style={{ border: "1px dashed #bbb", color: "#aaa" }}>
                  {el.label || "Image"}
                </div>
              )}
            </div>
          );
        } else if (el.type === "line") {
          body = (
            <div {...tid} style={{ ...common, width: el.w * scale, height: Math.max(6, (el.strokeWidth || 0.4) * scale), top: el.y * scale - 3, display: "flex", alignItems: "center" }}>
              <div style={{ width: "100%", height: Math.max(1, (el.strokeWidth || 0.4) * scale), background: el.color }} />
            </div>
          );
        } else if (el.type === "rect") {
          body = (
            <div {...tid} style={{
              ...common, width: el.w * scale, height: el.h * scale, boxSizing: "border-box",
              border: `${Math.max(1, (el.strokeWidth || 0.4) * scale)}px solid ${el.color}`,
              background: el.fill || "transparent",
            }} />
          );
        }
        return (
          <div key={el.id} onPointerDown={(e) => start(e, el, "move")} onPointerMove={move}
            onPointerUp={end} onPointerCancel={end}>
            {body}
            {sel && (
              <div
                onPointerDown={(e) => start(e, el, "resize")} onPointerMove={move} onPointerUp={end}
                className="absolute bg-amber-400"
                style={{
                  width: 10, height: 10, cursor: "nwse-resize",
                  left: (el.x + el.w) * scale - 5,
                  top: (el.type === "text" || el.type === "line" ? el.y * scale + 4 : (el.y + el.h) * scale) - 5,
                }}
                data-testid="studio-resize-handle"
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
